// MQL5 EA Import — upload an Expert Advisor's source and backtest it on our own market data.
//
// The page is deliberately ordered around the honest answer arriving FIRST: upload → capability
// report → backtest. An EA that cannot be reproduced faithfully says so by name, with the exact
// symbol or missing file, before anyone sees a number. A confident-looking equity curve for an EA
// we only half-understand would be the worst thing this feature could produce.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  FiUploadCloud, FiFileText, FiX, FiCheckCircle, FiAlertTriangle,
  FiPlayCircle, FiLoader, FiInfo, FiActivity,
} from 'react-icons/fi';
import {
  analyzeEa, backtestEa, readSourceFile, fetchImportMarkets, newProgressId, cancelImportRun,
  MQL5_IMPORT_TIMEFRAMES,
} from './mql5ImportFeeds';
import DeploySignalWizard from './DeploySignalWizard';
import './SignalsTab.css';
import './Mql5ImportPage.css';

// Which rejection dominated — the useful headline when nothing traded.
function mostCommonCode(entries) {
  const counts = new Map();
  entries.forEach((entry) => counts.set(entry.code, (counts.get(entry.code) || 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
}

const DEFAULT_SYMBOL = 'XAUUSD';

function isoDaysAgo(days) {
  const date = new Date(Date.now() - days * 86400000);
  return date.toISOString().slice(0, 10);
}

function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const number = Number(value);
  return `${number < 0 ? '-' : ''}$${Math.abs(number).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatWhen(seconds) {
  if (!seconds) return '—';
  return new Date(seconds * 1000).toISOString().slice(0, 16).replace('T', ' ');
}

// "Nice" axis bounds — round steps (1/2/2.5/5 × 10^n) so the ticks read $2,000 rather than $1,873.
function niceScale(min, max, targetTicks = 5) {
  const span = (max - min) || Math.abs(max) || 1;
  const rough = span / targetTicks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10) * magnitude;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let value = niceMin; value <= niceMax + step / 1000; value += step) ticks.push(Number(value.toFixed(10)));
  return { min: niceMin, max: niceMax, ticks };
}

function shortMoney(value) {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return `${sign}$${abs.toFixed(abs < 10 ? 2 : 0)}`;
}

// Dates across a test window: a year wants months, a week wants days, a day wants hours.
function formatAxisDate(seconds, spanSeconds) {
  const date = new Date(seconds * 1000);
  if (spanSeconds > 86400 * 200) return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
  if (spanSeconds > 86400 * 3) return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${String(date.getUTCHours()).padStart(2, '0')}:00`;
}

// Equity curve as an inline SVG — no chart dependency, but a chart with unlabelled axes is a
// decoration, not a result. Both axes carry ticks, gridlines and units, and the plot area is inset
// so the labels have somewhere to live. `preserveAspectRatio` is DEFAULT here (not "none"): the old
// version stretched the viewBox to the container and skewed every label with it.
// Balance and equity, MT5's two series. Equity below balance IS the open-position drawdown, so the
// gap between the lines is the chart's whole point — drawing equity alone hides it.
//
// The X AXIS is a choice, not a detail. Ours is real time, so a stretch where the EA does not trade
// is a flat plateau. MT5's report chart plots one point per DEAL, which gives idle periods zero
// width and makes the same run look continuously active. Both are honest; they answer different
// questions ("when did this happen" vs "what did each trade do"), and the toggle exists because a
// user comparing against an MT5 report should not have to guess why the shapes differ.
function EquityChart({ points, deposit, trades }) {
  const [mode, setMode] = useState('time');
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const series = useMemo(() => {
    const source = Array.isArray(points) ? points : [];
    if (mode === 'time') {
      return source.map((point) => ({
        t: Number(point.time) || 0,
        // Older payloads carried equity only; fall back rather than drawing a flat zero line.
        balance: Number.isFinite(Number(point.balance)) ? Number(point.balance) : Number(point.equity),
        equity: Number(point.equity),
      }));
    }
    const closed = (Array.isArray(trades) ? trades : [])
      .filter((trade) => Number.isFinite(Number(trade.exitTime)))
      .sort((a, b) => a.exitTime - b.exitTime);
    if (!closed.length) return [];
    // Balance is exact — it is the deposit plus realised profit in close order. Equity is the LOW
    // reached since the previous close, which is what MT5's green spikes are: the deepest the
    // account went while that basket was open.
    const out = [{ t: closed[0].exitTime, balance: deposit, equity: deposit }];
    let running = deposit;
    let cursor = 0;
    for (const trade of closed) {
      let low = Infinity;
      while (cursor < source.length && (Number(source[cursor].time) || 0) <= trade.exitTime) {
        const value = Number(source[cursor].equity);
        if (value < low) low = value;
        cursor += 1;
      }
      running += Number(trade.profit) || 0;
      out.push({ t: trade.exitTime, balance: running, equity: Number.isFinite(low) ? Math.min(low, running) : running });
    }
    return out;
  }, [mode, points, trades, deposit]);

  const width = 760;
  const height = 260;
  const pad = { top: 12, right: 16, bottom: 34, left: 62 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;

  const geometry = useMemo(() => {
    if (series.length < 2) return null;
    const lows = series.map((point) => Math.min(point.balance, point.equity));
    const highs = series.map((point) => Math.max(point.balance, point.equity));
    const scale = niceScale(Math.min(...lows, deposit), Math.max(...highs, deposit));
    const span = scale.max - scale.min || 1;
    const times = series.map((point) => point.t);
    const first = times[0];
    const last = times[times.length - 1];
    // Time mode positions by TIME, not by array index: the payload is thinned unevenly (buckets keep
    // their extremes), so index spacing no longer tracks the clock.
    const byTime = mode === 'time' && last > first;
    const timeSpan = last - first;
    const xOf = (index) => (byTime
      ? pad.left + ((times[index] - first) / timeSpan) * plotWidth
      : pad.left + (index / (series.length - 1)) * plotWidth);
    const yOf = (value) => pad.top + plotHeight - ((value - scale.min) / span) * plotHeight;
    const line = (key) => series
      .map((point, index) => `${index === 0 ? 'M' : 'L'}${xOf(index).toFixed(1)},${yOf(point[key]).toFixed(1)}`)
      .join(' ');
    return { scale, xOf, yOf, line, times, timeSpan: timeSpan || 1, byTime };
  }, [series, deposit, mode, plotWidth, plotHeight, pad.left, pad.top]);

  const onMove = useCallback((event) => {
    if (!geometry || !svgRef.current) return;
    const box = svgRef.current.getBoundingClientRect();
    // The SVG scales to its container, so client pixels must be mapped back into viewBox units.
    const local = ((event.clientX - box.left) / box.width) * width;
    let nearest = 0;
    let best = Infinity;
    for (let index = 0; index < series.length; index += 1) {
      const distance = Math.abs(geometry.xOf(index) - local);
      if (distance < best) { best = distance; nearest = index; }
    }
    setHover(nearest);
  }, [geometry, series, width]);

  if (!geometry) return null;
  const { scale, xOf, yOf, line, times, timeSpan } = geometry;
  const baseline = yOf(deposit);
  const last = series[series.length - 1];
  const hovered = hover === null ? null : series[hover];

  const xTickCount = 5;
  const xTicks = Array.from({ length: xTickCount }, (unused, slot) => {
    const index = Math.round((slot / (xTickCount - 1)) * (series.length - 1));
    return { index, label: times[index] > 0 ? formatAxisDate(times[index], timeSpan) : `#${index}` };
  });

  return (
    <figure className="mql5-import-chart">
      <figcaption>
        <span>Balance and equity over the test window</span>
        <span className="mql5-import-chart-modes" role="group" aria-label="Horizontal axis">
          <button type="button" className={mode === 'time' ? 'active' : ''}
            onClick={() => { setMode('time'); setHover(null); }}>By time</button>
          <button type="button" className={mode === 'trade' ? 'active' : ''}
            onClick={() => { setMode('trade'); setHover(null); }}>By trade</button>
        </span>
      </figcaption>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Balance ${formatMoney(last.balance)} and equity ${formatMoney(last.equity)} at the end of the run`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {scale.ticks.map((tick) => (
          <g key={`y-${tick}`}>
            <line x1={pad.left} y1={yOf(tick)} x2={width - pad.right} y2={yOf(tick)} className="mql5-import-chart-grid" />
            <text x={pad.left - 8} y={yOf(tick)} className="mql5-import-chart-tick" textAnchor="end" dominantBaseline="middle">
              {shortMoney(tick)}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <g key={`x-${tick.index}`}>
            <line x1={xOf(tick.index)} y1={pad.top} x2={xOf(tick.index)} y2={pad.top + plotHeight} className="mql5-import-chart-grid" />
            <text x={xOf(tick.index)} y={pad.top + plotHeight + 18} className="mql5-import-chart-tick" textAnchor="middle">
              {tick.label}
            </text>
          </g>
        ))}
        <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + plotHeight} className="mql5-import-chart-axis" />
        <line x1={pad.left} y1={pad.top + plotHeight} x2={width - pad.right} y2={pad.top + plotHeight} className="mql5-import-chart-axis" />
        <line x1={pad.left} y1={baseline} x2={width - pad.right} y2={baseline} className="mql5-import-chart-baseline" />
        <text x={width - pad.right} y={baseline - 5} className="mql5-import-chart-tick" textAnchor="end">
          deposit {shortMoney(deposit)}
        </text>

        {/* Equity beneath balance, so the drawdown gap reads as depth under the realised line. */}
        <path d={line('equity')} className="mql5-import-chart-line equity" />
        <path d={line('balance')} className="mql5-import-chart-line balance" />

        {hovered && (
          <g className="mql5-import-chart-cursor">
            <line x1={xOf(hover)} y1={pad.top} x2={xOf(hover)} y2={pad.top + plotHeight} />
            <circle cx={xOf(hover)} cy={yOf(hovered.equity)} r="3.5" className="equity" />
            <circle cx={xOf(hover)} cy={yOf(hovered.balance)} r="3.5" className="balance" />
          </g>
        )}

        <text x={pad.left - 48} y={pad.top + plotHeight / 2} className="mql5-import-chart-axis-title"
          textAnchor="middle" transform={`rotate(-90 ${pad.left - 48} ${pad.top + plotHeight / 2})`}>
          Account (USD)
        </text>
        <text x={pad.left + plotWidth / 2} y={height - 2} className="mql5-import-chart-axis-title" textAnchor="middle">
          {mode === 'time' ? 'Time (UTC)' : 'Closed trade'}
        </text>
      </svg>

      <div className="mql5-import-chart-legend">
        <span><i className="balance" /> Balance</span>
        <span><i className="equity" /> Equity{mode === 'trade' ? ' (low while open)' : ''}</span>
        {hovered ? (
          <span className="mql5-import-chart-readout">
            {hovered.t > 0 ? formatWhen(hovered.t) : `#${hover}`}
            {' · '}balance {formatMoney(hovered.balance)}
            {' · '}equity {formatMoney(hovered.equity)}
            {hovered.balance - hovered.equity > 0.005 && (
              <> · open loss {formatMoney(hovered.balance - hovered.equity)}</>
            )}
          </span>
        ) : (
          <span className="mql5-import-chart-readout muted">Hover the chart for any point</span>
        )}
      </div>
    </figure>
  );
}

// One EA input, rendered as the control its DECLARED TYPE calls for. MT5's own inputs dialogue
// never offers a text box for an enum or a bool, and a free-text field across thirty parameters is
// how a run ends up silently testing something the EA cannot express — `InpTimeframe` as "M15"
// instead of PERIOD_M15, a bool as "no", an int as 2.5.
function InputControl({ input, value, onChange }) {
  const common = {
    id: `ea-input-${input.name}`,
    value: value ?? '',
    onChange: (event) => onChange(event.target.value),
  };
  if (input.control === 'enum' && input.options?.length) {
    return (
      <select {...common}>
        {/* A default the source resolved to something outside the enum must still be shown, or the
            field would silently claim a value the EA never had. */}
        {!input.options.some((option) => option.value === value) && value !== '' && (
          <option value={value}>{value} (not a member)</option>
        )}
        {input.options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    );
  }
  if (input.control === 'bool') {
    return (
      <select {...common}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (input.control === 'int' || input.control === 'double') {
    return (
      <input
        {...common}
        type="number"
        step={input.control === 'int' ? 1 : 'any'}
        {...(Number.isFinite(input.min) ? { min: input.min } : {})}
        inputMode={input.control === 'int' ? 'numeric' : 'decimal'}
      />
    );
  }
  if (input.control === 'datetime') return <input {...common} type="date" />;
  if (input.control === 'color') return <input {...common} type="text" placeholder="clrRed or 0xRRGGBB" />;
  return <input {...common} type="text" />;
}

const PHASE_LABEL = {
  compiling: 'Preparing the test',
  downloading: 'Downloading market data',
  running: 'Running the strategy',
  summarizing: 'Building the report',
  done: 'Finishing',
  unknown: 'Starting',
};

function formatEta(seconds) {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

// What the server is actually doing. The percentage is a real ratio of bars executed or candles
// fetched — never a timer pretending to be progress.
function RunProgress({ progress, onCancel }) {
  const phase = progress?.phase || 'unknown';
  const total = Number(progress?.total) || 0;
  const loaded = Number(progress?.loaded) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : null;
  return (
    <div className="mql5-import-progress" role="status" aria-live="polite">
      <div className="mql5-import-progress-head">
        <span>{PHASE_LABEL[phase] || PHASE_LABEL.unknown}</span>
        <span className="mql5-import-progress-pct">
          {pct === null ? '' : `${pct}%`}
          {total > 0 && phase === 'running' ? ` · ${loaded.toLocaleString()} / ${total.toLocaleString()} bars` : ''}
          {total > 0 && phase === 'downloading' ? ` · ${loaded.toLocaleString()} / ${total.toLocaleString()} candles` : ''}
        </span>
      </div>
      {onCancel && (
        <button type="button" className="mql5-import-cancel" onClick={onCancel}>
          <FiX aria-hidden="true" /> Stop
        </button>
      )}
      <div className="mql5-import-progress-track">
        <div
          className={`mql5-import-progress-fill${pct === null ? ' indeterminate' : ''}`}
          style={pct === null ? undefined : { width: `${pct}%` }}
        />
      </div>
      {/* A heavy EA can sit on 4% for minutes while running perfectly well. The rate and the
          estimate are what separate "slow" from "hung" — without them the only honest reading of a
          still percentage is that something has broken. */}
      {(progress?.trades > 0 || progress?.etaSeconds > 0) && (
        <small className="mql5-import-progress-note">
          {progress.trades > 0 ? `${progress.trades.toLocaleString()} trades so far` : ''}
          {progress.trades > 0 && progress.etaSeconds > 0 ? ' · ' : ''}
          {progress.etaSeconds > 0 ? `about ${formatEta(progress.etaSeconds)} left` : ''}
          {progress.msPerBar >= 20 ? ` · ${Math.round(progress.msPerBar)}ms per bar` : ''}
        </small>
      )}
    </div>
  );
}

// Numbers go as numbers; anything else (an enum name like PERIOD_M15, or a string input) goes as
// text and the server resolves it against the MQL5 constant table.
//
// Shared by the backtest and the deploy, deliberately: deploying an EA with inputs resolved
// differently from the ones just backtested would make the backtest a description of a different
// strategy.
function resolveInputs(overrides) {
  const resolved = {};
  Object.entries(overrides || {}).forEach(([key, value]) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    const asNumber = Number(trimmed);
    resolved[key] = trimmed !== '' && Number.isFinite(asNumber) ? asNumber : trimmed;
  });
  return resolved;
}

export default function Mql5ImportPage() {
  const [files, setFiles] = useState([]);
  const [entry, setEntry] = useState('');
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [runId, setRunId] = useState('');
  const progressTimer = useRef(null);
  const [inputOverrides, setInputOverrides] = useState({});
  const [settings, setSettings] = useState({
    symbol: DEFAULT_SYMBOL,
    timeframe: 'H1',
    from: isoDaysAgo(365),
    to: isoDaysAgo(1),
    deposit: 10000,
    leverage: 100,
    spreadPoints: '',
  });
  const fileInputRef = useRef(null);
  // The deploy wizard, opened either by skipping the backtest or from the top of its results. It
  // is handed the analysed bundle rather than asking for the files again.
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployed, setDeployed] = useState(null);
  // Symbols and timeframes the server says it can load. Typing a symbol by hand is the easiest way
  // to get "only N bars available" for a name that does not exist, so it is a picker.
  const [markets, setMarkets] = useState({ symbols: [], timeframes: MQL5_IMPORT_TIMEFRAMES });

  useEffect(() => {
    let cancelled = false;
    fetchImportMarkets()
      .then((next) => {
        if (cancelled || !next?.symbols?.length) return;
        setMarkets({ symbols: next.symbols, timeframes: next.timeframes?.length ? next.timeframes : MQL5_IMPORT_TIMEFRAMES });
        // Keep the default only if the catalogue actually has it.
        setSettings((current) => (next.symbols.some((entry) => entry.symbol === current.symbol)
          ? current
          : { ...current, symbol: next.symbols[0].symbol }));
      })
      .catch(() => { /* the free-text fallback below still works */ });
    return () => { cancelled = true; };
  }, []);

  // Navigating away mid-run must not leave a poll running against a page that is gone.
  useEffect(() => () => clearInterval(progressTimer.current), []);

  // Grouped for the <optgroup>s — 107 symbols in one flat list is unusable.
  const symbolGroups = useMemo(() => {
    const groups = new Map();
    for (const entry of markets.symbols) {
      if (!groups.has(entry.assetClass)) groups.set(entry.assetClass, []);
      groups.get(entry.assetClass).push(entry);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [markets.symbols]);

  const entryCandidates = useMemo(() => files.filter((file) => /\.mq5$/i.test(file.name)), [files]);

  const addFiles = useCallback(async (fileList) => {
    setError('');
    const incoming = Array.from(fileList || []);
    const rejected = incoming.filter((file) => !/\.(mq5|mqh)$/i.test(file.name));
    const accepted = incoming.filter((file) => /\.(mq5|mqh)$/i.test(file.name));
    if (rejected.length) {
      setError(`Skipped ${rejected.map((file) => file.name).join(', ')} — only .mq5 and .mqh source files can be converted. A compiled .ex5 cannot be read.`);
    }
    if (!accepted.length) return;
    try {
      const read = await Promise.all(accepted.map(readSourceFile));
      setFiles((previous) => {
        const merged = [...previous.filter((file) => !read.some((next) => next.name === file.name)), ...read];
        return merged;
      });
      setReport(null);
      setResult(null);
      const firstEa = read.find((file) => /\.mq5$/i.test(file.name));
      if (firstEa) setEntry((current) => current || firstEa.name);
    } catch (readError) {
      setError(readError.message);
    }
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer.files);
  }, [addFiles]);

  const removeFile = (name) => {
    setFiles((previous) => previous.filter((file) => file.name !== name));
    setReport(null);
    setResult(null);
    if (entry === name) setEntry('');
  };

  const runAnalyze = async () => {
    setBusy('analyze');
    setError('');
    setResult(null);
    try {
      const next = await analyzeEa({ files, entry: entry || entryCandidates[0]?.name });
      setReport(next);
      // Prefill with what the EA's source actually says. For an enum that is the NAME
      // ("PERIOD_M15"), which the backend resolves — nobody should have to know M15 is 15.
      const defaults = {};
      (next.inputs || []).forEach((input) => {
        if (input.defaultLabel !== undefined && input.defaultLabel !== null && input.defaultLabel !== '') {
          defaults[input.name] = input.defaultLabel;
        } else if (input.defaultValue !== null && input.defaultValue !== undefined) {
          defaults[input.name] = input.defaultValue;
        } else {
          defaults[input.name] = '';
        }
      });
      setInputOverrides(defaults);
      // Pre-select the finest timeframe the EA reads. Higher ones aggregate automatically, so this
      // is the only choice that cannot fail — and it saves a round trip through a runtime error.
      if (next.timeframes?.finest) {
        setSettings((current) => ({ ...current, timeframe: next.timeframes.finest }));
      }
    } catch (failure) {
      setError(failure.message);
      if (failure.payload?.report) setReport(failure.payload.report);
    } finally {
      setBusy('');
    }
  };

  const runBacktest = async () => {
    setBusy('backtest');
    setError('');
    const progressId = newProgressId();
    setRunId(progressId);
    setProgress({ phase: 'compiling' });
    try {
      const numericInputs = resolveInputs(inputOverrides);
      // `settings` carries spreadPoints as a string, so it must be stripped BEFORE the spread —
      // otherwise the blank '' rides along and the conditional below adds nothing, which is exactly
      // how a blank field ended up asking the server for a zero spread.
      const { spreadPoints: spreadSetting, ...runSettings } = settings;
      const next = await backtestEa({
        files,
        entry: entry || entryCandidates[0]?.name,
        ...runSettings,
        deposit: Number(settings.deposit) || 10000,
        leverage: Number(settings.leverage) || 100,
        // Blank means "decide for me" — send nothing rather than a 0 that would read as a deliberate
        // zero spread.
        ...(String(spreadSetting ?? '').trim() === '' ? {} : { spreadPoints: Number(spreadSetting) || 0 }),
        inputs: numericInputs,
        progressId,
        // The run is detached server-side, so this drives the whole wait — there is no POST left
        // in flight to report against.
      }, { onProgress: setProgress });
      setResult(next);
      setReport(next.report);
    } catch (failure) {
      setError(failure.message);
      if (failure.payload?.report) setReport(failure.payload.report);
      if (failure.payload?.log) setResult({ log: failure.payload.log, failed: true });
    } finally {
      setProgress(null);
      setRunId('');
      setBusy('');
    }
  };

  const summary = result?.summary;
  const blockers = report
    ? [
      ...report.missingIncludes.map((entryRecord) => ({
        kind: 'Missing file',
        detail: `${entryRecord.spec} — included from ${entryRecord.fromFile}:${entryRecord.fromLine}. Upload it with the EA.`,
      })),
      ...report.dllImports.map((lib) => ({
        kind: 'DLL import',
        detail: `${lib} — external libraries cannot be reproduced, so this EA cannot be converted.`,
      })),
      // A deliberate refusal is NOT 'coming later' — saying so would have people waiting for a
      // feature that will never ship. It gets its own label and the actual reason.
      ...(report.refusals || []).map((refusal) => ({
        kind: 'Cannot convert',
        detail: `${refusal.symbol} ${refusal.reason}`,
      })),
      ...report.unresolved.map((symbol) => ({
        kind: 'Not yet',
        detail: `${symbol} is not implemented yet.`,
      })),
      ...report.parseErrors.map((parseError) => ({
        kind: 'Syntax',
        detail: `${parseError.file}:${parseError.line} ${parseError.message}`,
      })),
    ]
    : [];

  return (
    <HelmetProvider>
      <div className="mql5-import-page page-shell">
        <Helmet>
          <title>Import an MQL5 Expert Advisor | Vix Algo Trading</title>
          <meta name="description" content="Upload your MQL5 Expert Advisor source and backtest it in the cloud — no VPS, no terminal running." />
        </Helmet>

        <header className="mql5-import-hero">
          <h1>Import your Expert Advisor</h1>
          <p>
            Upload the <code>.mq5</code> source and any <code>.mqh</code> files it includes. We read the
            strategy, tell you exactly what we can and cannot reproduce, and run it on our market data.
          </p>
          <p className="mql5-import-hero-note">
            <FiInfo aria-hidden="true" />
            Your source is used for this request only and is never stored. A compiled <code>.ex5</code>
            {' '}cannot be converted — the source is required.
          </p>
        </header>

        {/* ── 1. Upload ── */}
        <section className="mql5-import-card">
          <h2>1 · Source files</h2>
          <div
            className={`mql5-import-drop${dragging ? ' dragging' : ''}`}
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click(); }}
            role="button"
            tabIndex={0}
          >
            <FiUploadCloud aria-hidden="true" />
            <span>Drop <strong>.mq5</strong> and <strong>.mqh</strong> files here, or click to browse</span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".mq5,.mqh"
              onChange={(event) => addFiles(event.target.files)}
              hidden
            />
          </div>

          {files.length > 0 && (
            <ul className="mql5-import-files">
              {files.map((file) => (
                <li key={file.name}>
                  <FiFileText aria-hidden="true" />
                  <span className="mql5-import-file-name">{file.name}</span>
                  <span className="mql5-import-file-size">{(file.content.length / 1024).toFixed(1)} KB</span>
                  <button type="button" onClick={() => removeFile(file.name)} aria-label={`Remove ${file.name}`}>
                    <FiX aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {entryCandidates.length > 1 && (
            <label className="mql5-import-field">
              <span>Expert Advisor</span>
              <select value={entry} onChange={(event) => setEntry(event.target.value)}>
                {entryCandidates.map((file) => <option key={file.name} value={file.name}>{file.name}</option>)}
              </select>
            </label>
          )}

          <button
            type="button"
            className="mql5-import-primary"
            disabled={!files.length || busy === 'analyze'}
            onClick={runAnalyze}
          >
            {busy === 'analyze' ? <><FiLoader className="spin" aria-hidden="true" /> Reading…</> : 'Analyze'}
          </button>
        </section>

        {error && <div className="mql5-import-error" role="alert">{error}</div>}

        {/* ── 2. Capability report ── */}
        {report && (
          <section className="mql5-import-card">
            <h2>2 · What we found</h2>
            <div className={`mql5-import-verdict ${report.ok ? 'ok' : 'blocked'}`}>
              {report.ok
                ? <><FiCheckCircle aria-hidden="true" /> <span><strong>{report.entry}</strong> can run. Every symbol it uses is supported.</span></>
                : <><FiAlertTriangle aria-hidden="true" /> <span><strong>{report.entry}</strong> cannot run yet — {blockers.length} item{blockers.length === 1 ? '' : 's'} below.</span></>}
            </div>

            <dl className="mql5-import-facts">
              <div><dt>Expert Advisor</dt><dd>{report.isEa ? 'Yes — OnTick found' : 'No OnTick found'}</dd></div>
              <div>
                <dt>Execution</dt>
                <dd>{report.execution?.mode === 'tick' ? 'Every tick' : report.execution?.mode === 'bar' ? 'Once per bar' : 'Unknown'}</dd>
              </div>
              <div><dt>Inputs</dt><dd>{report.inputs.length}</dd></div>
            </dl>

            {blockers.length > 0 && (
              <ul className="mql5-import-blockers">
                {blockers.map((blocker) => (
                  <li key={`${blocker.kind}-${blocker.detail}`}>
                    <span className="mql5-import-blocker-kind">{blocker.kind}</span>
                    <span>{blocker.detail}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Its own callout rather than a warning bullet: this one qualifies every number the
                run is about to produce, and it is the only difference between "the backtest predicts
                the live signal" and "the backtest is indicative". */}
            {report.execution && !report.execution.matchesBacktest && (
              <div className="mql5-import-execution" role="note">
                <FiActivity aria-hidden="true" />
                <div>
                  <strong>This EA runs on every tick; the tester runs one evaluation per bar.</strong>
                  <p>{report.execution.note}</p>
                  <p className="mql5-import-execution-live">
                    Deployed as a live signal it runs on every tick, the way it does in your terminal —
                    the per-bar limit is the tester&apos;s, not the platform&apos;s.
                  </p>
                </div>
              </div>
            )}

            {report.warnings?.length > 0 && (
              <ul className="mql5-import-warnings">
                {report.warnings.map((warning) => <li key={warning}><FiAlertTriangle aria-hidden="true" /> {warning}</li>)}
              </ul>
            )}
          </section>
        )}

        {/* ── 3. Settings and run ── */}
        {report?.ok && (
          <section className="mql5-import-card">
            <h2>3 · Backtest</h2>
            <div className="mql5-import-settings">
              <label className="mql5-import-field">
                <span>Symbol</span>
                {symbolGroups.length ? (
                  <select value={settings.symbol} onChange={(event) => setSettings({ ...settings, symbol: event.target.value })}>
                    {symbolGroups.map(([assetClass, entries]) => (
                      <optgroup key={assetClass} label={assetClass}>
                        {entries.map((entry) => (
                          <option key={entry.symbol} value={entry.symbol}>
                            {entry.label === entry.symbol ? entry.symbol : `${entry.symbol} — ${entry.label}`}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                ) : (
                  <input value={settings.symbol} onChange={(event) => setSettings({ ...settings, symbol: event.target.value.toUpperCase() })} />
                )}
              </label>
              <label className="mql5-import-field">
                <span>Timeframe</span>
                <select value={settings.timeframe} onChange={(event) => setSettings({ ...settings, timeframe: event.target.value })}>
                  {markets.timeframes.map((timeframe) => <option key={timeframe} value={timeframe}>{timeframe}</option>)}
                </select>
                {report?.timeframes?.finest && (
                  <small className="mql5-import-hint">
                    reads {report.timeframes.referenced.join(', ')} — use {report.timeframes.finest} or finer
                  </small>
                )}
              </label>
              <label className="mql5-import-field">
                <span>From</span>
                <input type="date" value={settings.from} onChange={(event) => setSettings({ ...settings, from: event.target.value })} />
              </label>
              <label className="mql5-import-field">
                <span>To</span>
                <input type="date" value={settings.to} onChange={(event) => setSettings({ ...settings, to: event.target.value })} />
              </label>
              <label className="mql5-import-field">
                <span>Deposit</span>
                <input type="number" min="100" value={settings.deposit} onChange={(event) => setSettings({ ...settings, deposit: event.target.value })} />
              </label>
              <label className="mql5-import-field">
                <span>Leverage</span>
                <input type="number" min="1" value={settings.leverage} onChange={(event) => setSettings({ ...settings, leverage: event.target.value })} />
              </label>
              <label className="mql5-import-field">
                <span>Spread (points)</span>
                <input
                  type="number"
                  min="0"
                  placeholder="auto"
                  value={settings.spreadPoints}
                  onChange={(event) => setSettings({ ...settings, spreadPoints: event.target.value })}
                />
                {/* Our price feed carries no bid/ask. Left empty this falls back to the platform's
                    sampled spread, and to zero if there is none — which flatters every result. */}
                <small className="mql5-import-hint">blank = use the sampled spread</small>
              </label>
            </div>

            {report.inputs.length > 0 && (
              <details className="mql5-import-inputs">
                <summary>EA inputs ({report.inputs.length})</summary>
                <div className="mql5-import-settings">
                  {report.inputs.map((input) => (
                    <div className="mql5-import-field" key={input.name}>
                      <label htmlFor={`ea-input-${input.name}`}>
                        <span title={input.type}>{input.name}</span>
                        <small className="mql5-import-field-type">{input.type}</small>
                      </label>
                      <InputControl
                        input={input}
                        value={inputOverrides[input.name] ?? ''}
                        onChange={(next) => setInputOverrides({ ...inputOverrides, [input.name]: next })}
                      />
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Two ways forward, and the recommended one is the default-looking button. Testing an
                EA against history before putting it live is the whole point of this page, but
                someone who has already tested it elsewhere should not be forced through it. */}
            <div className="mql5-import-actions">
              <button type="button" className="mql5-import-primary" disabled={busy === 'backtest'} onClick={runBacktest}>
                {busy === 'backtest'
                  ? <><FiLoader className="spin" aria-hidden="true" /> Running…</>
                  : <><FiPlayCircle aria-hidden="true" /> Run backtest <small>(recommended)</small></>}
              </button>
              <button
                type="button"
                className="mql5-import-secondary"
                disabled={busy === 'backtest'}
                onClick={() => setDeployOpen(true)}
              >
                Skip backtest — deploy now
              </button>
            </div>

            {busy === 'backtest' && (
          <RunProgress
            progress={progress}
            onCancel={runId ? () => { cancelImportRun(runId); setProgress({ phase: 'cancelling' }); } : null}
          />
        )}
          </section>
        )}

        {/* ── 4a. Cancelled before a single bar ran ──
            A run stopped during the history download has no window, no bars and no trades, so the
            results table below would be a row of zeros over an undefined date range. Say what
            happened instead. */}
        {result?.cancelled && !result.bars && (
          <section className="mql5-import-card">
            <h2>4 · Cancelled</h2>
            <p className="mql5-import-window">
              Stopped {result.cancelledDuring === 'download'
                ? 'while the history was still downloading'
                : 'before the backtest started'} — no bars ran, so there is nothing to report.
              {' '}Run it again to start over.
            </p>
          </section>
        )}

        {/* ── 4. Results ── */}
        {summary && !(result?.cancelled && !result.bars) && (
          <section className="mql5-import-card">
            {/* Deploy sits at the TOP of the results, because this is the moment the decision gets
                made — having just seen the numbers, not after scrolling past a trade table. */}
            <div className="mql5-import-result-head">
              <h2>4 · Result</h2>
              <button type="button" className="mql5-import-primary" onClick={() => setDeployOpen(true)}>
                <FiActivity aria-hidden="true" /> Deploy as live signal
              </button>
            </div>
            <p className="mql5-import-window">
              {result.symbol} {result.timeframe} · {Number(result.bars || 0).toLocaleString()} bars ·
              {' '}{String(result.from || '').slice(0, 10)} → {String(result.to || '').slice(0, 10)}
              {' '}· ran in {((result.elapsedMs || 0) / 1000).toFixed(1)}s
            </p>

            {/* The window that ran is not the window that was asked for. Saying so here, next to the
                dates, is the difference between a helpful trim and a silently different test. */}
            {result.rangeAdjusted && (
              <p className="mql5-import-trimmed" role="note">
                <FiInfo aria-hidden="true" />
                <span>
                  Range shortened to fit: you asked from <strong>{result.rangeAdjusted.requestedFrom}</strong>,
                  {' '}the test ran from <strong>{result.rangeAdjusted.from}</strong>. {result.rangeAdjusted.reason}
                  {' '}For a longer span, use a higher timeframe.
                </span>
              </p>
            )}

            <div className="mql5-import-stats">
              <div><span>Trades</span><strong>{summary.trades}</strong></div>
              <div><span>Net profit</span><strong className={summary.netProfit >= 0 ? 'pos' : 'neg'}>{formatMoney(summary.netProfit)}</strong></div>
              <div><span>Profit factor</span><strong>{summary.profitFactor ?? '—'}</strong></div>
              <div><span>Win rate</span><strong>{summary.winRate === null ? '—' : `${summary.winRate}%`}</strong></div>
              <div><span>Max drawdown</span><strong className="neg">{formatMoney(summary.maxDrawdown)}</strong></div>
              <div><span>Final balance</span><strong>{formatMoney(summary.finalBalance)}</strong></div>
            </div>

            {/* Repeated here on purpose. Section 2 is read once, before the run; these numbers are
                what gets screenshotted and compared against a terminal. */}
            {result.report?.execution && !result.report.execution.matchesBacktest && (
              <p className="mql5-import-execution-inline" role="note">
                <FiActivity aria-hidden="true" />
                <span>
                  Open prices only — one evaluation per bar. This EA acts on every tick, so a live
                  deployment evaluates more often than this backtest did and results will differ.
                </span>
              </p>
            )}

            {summary.accountBlown && (
              <div className="mql5-import-error" role="alert">
                The account hit a margin stop-out during this run — every position was force-closed, exactly as a broker would.
              </div>
            )}

            {/* "0 trades" on its own reads as a broken feature. When the broker rejected every order
                there is a specific reason for it, and showing that reason IS the result. */}
            {summary.trades === 0 && (
              <div className="mql5-import-empty" role="status">
                <FiAlertTriangle aria-hidden="true" />
                <div>
                  <strong>This EA placed no trades over that window.</strong>
                  {result.journalErrors?.length > 0 ? (
                    <>
                      <p>
                        {result.journalErrors.length} order{result.journalErrors.length === 1 ? ' was' : 's were'} rejected.
                        {' '}The most common reason was <code>{mostCommonCode(result.journalErrors)}</code>:
                      </p>
                      <p className="mql5-import-empty-quote">{result.journalErrors[0].message}</p>
                      {mostCommonCode(result.journalErrors) === 'invalid_stops' && (
                        <p>
                          {result.spec.spreadPoints > 0
                            ? `The spread on ${result.symbol} is ${Math.round(result.spec.spreadPoints)} points, which is wider than the stop distance this EA asks for.`
                            : `The spread on ${result.symbol} is wider than the stop distance this EA asks for.`}
                          {' '}Its target lands on the wrong side of the entry, so the broker refuses the order — a real
                          terminal would do the same. Raise the EA&apos;s take-profit input above the spread, or try a
                          symbol that trades tighter.
                        </p>
                      )}
                    </>
                  ) : (
                    <p>Its entry conditions were never met on this symbol, timeframe and date range.</p>
                  )}
                </div>
              </div>
            )}

            <EquityChart points={result.equity} deposit={result.deposit} trades={result.trades} />

            {/* The contract specification IS part of the result. A minimum volume that is wrong by
                20x changes every figure above, and there is no way to notice that from the numbers
                alone — so the values actually used are stated, with where they came from. */}
            {result.spec && (
              <details className="mql5-import-inputs" open={result.specMeta?.notes?.length > 0}>
                <summary>
                  Contract specification
                  {result.specMeta?.confidence === 'broker'
                    ? ' — from your MT5 broker export'
                    : ` — ${result.specMeta?.confidence || 'unknown'} (not from a broker feed)`}
                </summary>
                <dl className="mql5-import-facts">
                  <div><dt>Min / step / max volume</dt><dd>{result.spec.volumeMin} / {result.spec.volumeStep} / {result.spec.volumeMax}</dd></div>
                  <div><dt>Contract size</dt><dd>{result.spec.contractSize}</dd></div>
                  <div><dt>Digits / point</dt><dd>{result.spec.digits} / {result.spec.point}</dd></div>
                  <div><dt>Stops level</dt><dd>{result.spec.stopsLevel} points</dd></div>
                  <div>
                    <dt>Margin per lot</dt>
                    <dd>{result.spec.marginInitialPerLot > 0 ? formatMoney(result.spec.marginInitialPerLot) : `derived from 1:${result.spec.leverage}`}</dd>
                  </div>
                  <div><dt>Spread</dt><dd>{Math.round(result.spec.spreadPoints)} points</dd></div>
                </dl>
                {result.specMeta?.notes?.length > 0 && (
                  <ul className="mql5-import-warnings">
                    {result.specMeta.notes.map((note) => (
                      <li key={note}><FiAlertTriangle aria-hidden="true" /> {note}</li>
                    ))}
                  </ul>
                )}
              </details>
            )}

            {result.trades?.length > 0 && (
              <div className="mql5-import-table-shell">
                <table className="mql5-import-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Side</th><th>Volume</th><th>Entry</th><th>Price</th>
                      <th>Exit</th><th>Price</th><th>Why</th><th>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades.slice(0, 200).map((trade, index) => (
                      <tr key={`${trade.ticket}-${trade.exitTime}-${index}`}>
                        <td>{index + 1}</td>
                        <td className={trade.side}>{trade.side}</td>
                        <td>{trade.volume}</td>
                        <td>{formatWhen(trade.entryTime)}</td>
                        <td>{trade.entryPrice}</td>
                        <td>{formatWhen(trade.exitTime)}</td>
                        <td>{trade.exitPrice}</td>
                        <td>{trade.reason}</td>
                        <td className={trade.profit >= 0 ? 'pos' : 'neg'}>{formatMoney(trade.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.trades.length > 200 && <p className="mql5-import-more">Showing the first 200 of {result.trades.length} trades.</p>}
              </div>
            )}

            {result.journalErrors?.length > 0 && (
              <details className="mql5-import-log">
                <summary>Rejected orders and errors ({result.journalErrors.length})</summary>
                <ul>
                  {result.journalErrors.map((journalEntry, index) => (
                    <li key={`${journalEntry.barIndex}-${index}`}>bar {journalEntry.barIndex} · {journalEntry.code} · {journalEntry.message}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        )}

        {result?.log?.length > 0 && (
          <section className="mql5-import-card">
            <details className="mql5-import-log" open={Boolean(result.failed)}>
              <summary>EA output ({result.log.length} lines)</summary>
              <pre>{result.log.join('\n')}</pre>
            </details>
          </section>
        )}

        {deployed && (
          <section className="mql5-import-card">
            <h2>Deployed</h2>
            <p className="mql5-import-window">
              <strong>{deployed.name}</strong> is live on {deployed.symbol} {deployed.timeframe}.
            </p>
            <a className="mql5-import-primary" href={`/signals/${deployed.id}`}>Open the signal</a>
          </section>
        )}

        {/* The same wizard the Builder uses, handed the EA that was just analysed — and its
            backtest result when there is one, so the review step can say whether this was tested. */}
        {deployOpen && report?.ok && (
          <DeploySignalWizard
            eaContext={{
              files,
              entry: entry || report.entry,
              report,
              symbol: settings.symbol,
              timeframe: settings.timeframe,
              inputs: resolveInputs(inputOverrides),
              leverage: Number(settings.leverage) || 500,
              backtest: summary
                ? {
                  trades: summary.trades,
                  netProfit: summary.netProfit,
                  profitFactor: summary.profitFactor,
                }
                : null,
            }}
            onClose={() => setDeployOpen(false)}
            onDeployed={(deployment) => {
              setDeployOpen(false);
              setDeployed(deployment);
            }}
          />
        )}
      </div>
    </HelmetProvider>
  );
}
